import {Dispatch, SetStateAction, useEffect, useState} from "react";

const PREFIX = 'tsa-ui:';

// useState that's remembered in this browser (localStorage) across navigation and reloads, e.g. a table's
// filter and sort. isValid checks what's read back, so stale or hand-edited values fall back to the initial one.
export function usePersistentState<T>(key: string, initial: T, isValid?: (value: unknown) => value is T): [T, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState<T>(() => {
        try {
            const json = localStorage.getItem(PREFIX + key);
            if (json !== null) {
                const stored: unknown = JSON.parse(json);
                if (!isValid || isValid(stored)) {
                    return stored as T;
                }
            }
        } catch {
            // Storage unavailable or unreadable: start from the initial value.
        }
        return initial;
    });

    useEffect(() => {
        try {
            if (value === undefined) {
                localStorage.removeItem(PREFIX + key);
            } else {
                localStorage.setItem(PREFIX + key, JSON.stringify(value));
            }
        } catch {
            // Storage unavailable (private mode): the value lasts as long as the page.
        }
    }, [key, value]);

    return [value, setValue];
}
